import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Select from "react-select";
import { getCustomSelectStyles } from "../../../../shared";
import { allUnitsQueryOptions } from "../../../../../../features/units/queries";
import { useCompaniesStore } from "../../../../../../store/admin/companiesStore";
import type { ThemeClasses } from "../types";

interface VehicleOption {
  value: string;
  label: string;
  deviceId: string;
}

interface TrackingReplayCreateModalProps {
  isOpen: boolean;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onClose: () => void;
  onStartRecording: (config: RecordingConfig) => void;
}

export interface RecordingConfig {
  vehicleId: string;
  vehicleName: string;
  deviceId: string;
  recordingName: string;
  durationHours: number;
}

export function TrackingReplayCreateModal({
  isOpen,
  isDarkMode,
  themeClasses,
  onClose,
  onStartRecording,
}: TrackingReplayCreateModalProps) {
  const { t } = useTranslation("adminSections");
  const { t: tCommon } = useTranslation("common");

  const [recordingName, setRecordingName] = useState("");
  const [selectedDurationHours, setSelectedDurationHours] = useState(1);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);

  // Unidades (vehículos) desde la cache compartida; solo se piden al abrir el modal
  const companyId = useCompaniesStore((s) => s.companyId);
  const unitsQuery = useQuery({
    ...allUnitsQueryOptions(companyId),
    enabled: isOpen && !!companyId,
  });
  const units = unitsQuery.data ?? [];
  const unitsLoading = unitsQuery.isLoading;

  // Opciones de vehículos para el select
  const vehicleOptions: VehicleOption[] = useMemo(() => {
    return units.map((unit) => ({
      value: unit.id,
      label: unit.name || unit.plate || unit.id,
      deviceId: unit.devices?.[0]?.id || "",
    }));
  }, [units]);

  const selectedVehicle: VehicleOption | null = useMemo(() => {
    return vehicleOptions.find((opt) => opt.value === selectedVehicleId) || null;
  }, [vehicleOptions, selectedVehicleId]);

  const handleSubmit = useCallback(() => {
    if (!recordingName.trim()) {
      return;
    }
    if (!selectedVehicleId || !selectedVehicle) {
      return;
    }

    const config: RecordingConfig = {
      vehicleId: selectedVehicleId,
      vehicleName: selectedVehicle.label,
      deviceId: selectedVehicle.deviceId,
      recordingName: recordingName.trim(),
      durationHours: selectedDurationHours,
    };

    onStartRecording(config);
  }, [recordingName, selectedDurationHours, selectedVehicleId, selectedVehicle, onStartRecording]);

  const handleClose = useCallback(() => {
    setRecordingName("");
    setSelectedDurationHours(1);
    setSelectedVehicleId(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tracking-replay-create-title"
    >
      <div className={`rounded-lg p-6 w-full max-w-md ${themeClasses.cardBg} border ${themeClasses.cardBorder}`}>
        <div className="flex items-center justify-between mb-4">
          <h3
            id="tracking-replay-create-title"
            className={`text-lg font-semibold ${themeClasses.text}`}
          >
            {t("trackingReplays.createTitle")}
          </h3>
          <button
            onClick={handleClose}
            className={`p-2 rounded-lg ${themeClasses.buttonHover} transition-colors`}
          >
            <i className={`ri-close-line text-xl ${themeClasses.text}`}></i>
          </button>
        </div>

        <p className={`text-xs mb-4 ${themeClasses.textSecondary}`}>
          {t("trackingReplays.createHint")}
        </p>

        {/* Selector de Vehículo */}
        <label className={`block text-xs font-medium mb-1 ${themeClasses.textSecondary}`}>
          {t("trackingReplays.selectVehicle")}
        </label>
        <Select
          options={vehicleOptions}
          styles={getCustomSelectStyles(isDarkMode)}
          placeholder={t("trackingReplays.selectVehiclePlaceholder")}
          value={selectedVehicle}
          onChange={(option: any) => {
            setSelectedVehicleId(option?.value || null);
            if (option && !recordingName) {
              setRecordingName(`${option.label} — ${new Date().toLocaleString()}`);
            }
          }}
          isSearchable
          isClearable
          isLoading={unitsLoading}
          noOptionsMessage={() => t("trackingReplays.noVehiclesAvailable")}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          className="mb-4"
        />

        {/* Nombre del recorrido */}
        <label className={`block text-xs font-medium mb-1 ${themeClasses.textSecondary}`}>
          {t("trackingReplays.recordingName")}
        </label>
        <input
          type="text"
          value={recordingName}
          onChange={(e) => setRecordingName(e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg mb-4 text-sm ${
            isDarkMode 
              ? "bg-dark-card border-dark-border text-white" 
              : "bg-white border-gray-300 text-gray-900"
          }`}
          placeholder={t("trackingReplays.recordingNamePlaceholder")}
        />

        {/* Duración */}
        <label className={`block text-xs font-medium mb-1 ${themeClasses.textSecondary}`}>
          {t("trackingReplays.recordingDuration")}
        </label>
        <select
          value={selectedDurationHours}
          onChange={(e) => setSelectedDurationHours(Number(e.target.value))}
          className={`w-full px-3 py-2 border rounded-lg mb-4 text-sm ${
            isDarkMode 
              ? "bg-dark-card border-dark-border text-white" 
              : "bg-white border-gray-300 text-gray-900"
          }`}
        >
          {Array.from({ length: 24 }, (_, i) => i + 1).map((hours) => (
            <option key={hours} value={hours}>
              {hours} {hours === 1 ? tCommon("hour") : tCommon("hours")}
            </option>
          ))}
        </select>

        {/* Info */}
        <div className={`text-xs mb-4 p-2 rounded ${
          isDarkMode 
            ? "bg-blue-900/30 text-blue-300" 
            : "bg-blue-50 text-blue-700"
        }`}>
          <i className="ri-information-line mr-1"></i>
          {t("trackingReplays.createInfo")}
        </div>

        {/* Botones */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleClose}
            className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium ${
              isDarkMode 
                ? "bg-gray-700 text-gray-300 hover:bg-gray-600" 
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            } transition-colors`}
          >
            {tCommon("cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!recordingName.trim() || !selectedVehicleId}
            className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-external-link-line"></i>
            {t("trackingReplays.startRecording")}
          </button>
        </div>
      </div>
    </div>
  );
}
